import { useOrganization } from '@clerk/clerk-react';

/** Plan hierarchy — each plan includes all features from plans below it */
export type Plan = 'free' | 'starter' | 'growth' | 'scale' | 'enterprise';

const PLAN_RANK: Record<Plan, number> = {
  free: 0,
  starter: 1,
  growth: 2,
  scale: 3,
  enterprise: 4,
};

/** Map of feature flags to the minimum plan required */
export const FEATURE_PLAN_MAP: Record<string, Plan> = {
  team_chat: 'starter',
  agent_coaching: 'growth',
  ai_quality_scoring: 'growth',
  ai_suggestions: 'starter',
  advanced_analytics: 'growth',
  custom_flows: 'starter',
  sla_management: 'starter',
  marketplace: 'free',
  knowledge_base: 'free',
  csat_surveys: 'starter',
  bulk_actions: 'starter',
  api_access: 'growth',
  proactive_engagement: 'growth',
  in_chat_payments: 'starter',
  sentiment_alerts: 'growth',
  auto_escalation: 'growth',
  white_label: 'enterprise',
  custom_roles: 'enterprise',
  audit_logs: 'growth',
  benchmark_reports: 'growth',
  per_site_identity: 'growth',
  sms: 'enterprise', // SMS & WhatsApp — Enterprise only (usage-billed add-on; matches PLAN_LIMITS.sms)
};

/** Returns the current organisation's plan, falling back to 'free' (or 'growth' in dev) */
export function usePlan(): Plan {
  const { organization } = useOrganization();
  const raw = (organization?.publicMetadata as Record<string, unknown>)?.plan;
  if (typeof raw === 'string' && raw in PLAN_RANK) return raw as Plan;
  // In development, default to growth so all features are accessible for testing.
  // In production, unset metadata falls back to 'free'.
  if (import.meta.env.DEV) return 'growth';
  return 'free';
}

/**
 * Returns true if the current plan meets or exceeds the required plan for `feature`.
 * `feature` is optional so callers can invoke this hook unconditionally (Rules of
 * Hooks): an absent or unknown feature is allowed by default.
 */
export function useFeature(feature?: string): boolean {
  const plan = usePlan();
  const required = feature ? FEATURE_PLAN_MAP[feature] : undefined;
  if (!required) return true; // no/unknown feature — allow by default
  return PLAN_RANK[plan] >= PLAN_RANK[required];
}
